/**
 * ProgressDots.jsx
 *
 * Wizard progress indicator. Renders one dot per step in the current
 * path's sequence (from progressConfig.sequence), with state:
 *   - Completed steps: solid electric-blue
 *   - Current step:    electric-blue with a light ring and slight scale-up
 *   - Upcoming steps:  neutral n3
 * Sub-steps (EQUIPMENT_THREAD_CHOICE, THREAD_CHOICE) render as smaller dots.
 *
 * Ported from Top Hammer ToolFinder v4 (src/components/ui/ProgressDots.jsx),
 * with raw hex colours replaced by Sandvik Tailwind tokens.
 *
 * @module components/wizard/ProgressDots
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D6)
 */

import { useWizard } from '@contexts/WizardContext';
import { STEPS } from './wizardConstants';

const STEP_LABELS = {
  [STEPS.APPLICATION]: 'Application',
  [STEPS.DRILLING_METHOD]: 'Method',
  [STEPS.EQUIPMENT_CLASS]: 'Equipment',
  [STEPS.EQUIPMENT_THREAD_CHOICE]: 'Thread',
  [STEPS.HOLE_DIAMETER]: 'Hole size',
  [STEPS.ROCK_CONDITIONS]: 'Rock',
  [STEPS.EQUIPMENT_SELECT]: 'Equipment',
  [STEPS.THREAD_CHOICE]: 'Thread',
  [STEPS.SUMMARY]: 'Summary',
};

const SUB_STEPS = new Set([STEPS.EQUIPMENT_THREAD_CHOICE, STEPS.THREAD_CHOICE]);

export default function ProgressDots() {
  const { progressConfig } = useWizard();
  const { sequence = [], currentIndex } = progressConfig;
  if (sequence.length === 0) return null;

  return (
    <nav
      aria-label="Wizard progress"
      className="flex items-center justify-center gap-2 px-4"
    >
      {sequence.map((step, idx) => {
        const isCurrent = idx === currentIndex;
        const isComplete = idx < currentIndex;
        const isSub = SUB_STEPS.has(step);
        const sizeClass = isSub ? 'w-2 h-2' : 'w-3 h-3';
        const stateClass = isCurrent
          ? 'bg-electric-blue ring-4 ring-electric-blue/20 scale-110'
          : isComplete
            ? 'bg-electric-blue'
            : 'bg-n3';
        return (
          <div key={`${step}-${idx}`} className="flex items-center">
            <div
              className={`${sizeClass} ${stateClass} rounded-full transition-all`}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={STEP_LABELS[step] || step}
            />
            {idx < sequence.length - 1 && (
              <div
                className={`w-6 h-px mx-1 ${isComplete ? 'bg-electric-blue' : 'bg-n3'}`}
              />
            )}
          </div>
        );
      })}
      <span className="ml-3 text-xs text-n6">
        Step {currentIndex + 1} of {sequence.length}:{' '}
        <span className="font-normal text-n9">
          {STEP_LABELS[sequence[currentIndex]] || sequence[currentIndex]}
        </span>
      </span>
    </nav>
  );
}
